import { useState, useMemo } from 'react';
import { useProducts } from '../../hooks/useProducts';
import { Product, ProductCategory, categoryConfig } from '../../mocks/products';
import { agencyInfo } from '../../mocks/dashboard';
import ProductCard from './components/ProductCard';
import ProductDetailModal from './components/ProductDetailModal';
import AddProductModal from './components/AddProductModal';

type CategoryTab = ProductCategory | 'All';

export default function ProductsPage() {
  const brand = agencyInfo.brandColor;
  const { products, loading } = useProducts();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [categoryTab, setCategoryTab] = useState<CategoryTab>('All');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'sellPrice' | 'margin' | 'booked'>('booked');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [statusFilter, setStatusFilter] = useState<'All' | 'Active' | 'Inactive' | 'Sold Out'>('All');

  const categories: CategoryTab[] = ['All', 'Package', 'Flight', 'Hotel', 'Tour', 'Visa', 'Transfer', 'Insurance', 'Cruise'];

  const filtered = useMemo(() => {
    let list = [...products];
    if (categoryTab !== 'All') list = list.filter((p) => p.category === categoryTab);
    if (statusFilter !== 'All') list = list.filter((p) => p.status === statusFilter);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((p) =>
        p.name.toLowerCase().includes(s) ||
        p.destination.toLowerCase().includes(s) ||
        p.tags.some((t) => t.toLowerCase().includes(s))
      );
    }
    list.sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'sellPrice') return b.sellPrice - a.sellPrice;
      if (sortBy === 'margin') {
        const mA = (a.sellPrice - a.costPrice) / a.sellPrice;
        const mB = (b.sellPrice - b.costPrice) / b.sellPrice;
        return mB - mA;
      }
      return b.timesBooked - a.timesBooked;
    });
    return list;
  }, [products, categoryTab, search, sortBy, statusFilter]);

  const totalRevenue = products.reduce((s, p) => s + p.timesBooked * p.sellPrice, 0);
  const avgMargin = products.length > 0 ? Math.round(products.reduce((s, p) => s + ((p.sellPrice - p.costPrice) / p.sellPrice) * 100, 0) / products.length) : 0;
  const featured = products.filter((p) => p.featured).length;

  const fmtMoney = (n: number) => `${agencyInfo.currency} ${(n / 1000).toFixed(0)}K`;

  const kpis = [
    { label: 'Total Products', value: products.length, sub: `${featured} featured`, icon: 'ri-price-tag-3-line', color: brand },
    { label: 'Active Products', value: products.filter((p) => p.status === 'Active').length, sub: 'Available to book', icon: 'ri-check-double-line', color: '#10B981' },
    { label: 'Total Revenue', value: fmtMoney(totalRevenue), sub: 'From all bookings', icon: 'ri-money-dollar-circle-line', color: '#D97706' },
    { label: 'Avg Margin', value: `${avgMargin}%`, sub: 'Across catalog', icon: 'ri-percent-line', color: '#0891B2' },
  ];

  const handleEdit = (p: Product) => {
    setSelectedProduct(null);
    setEditProduct(p);
    setShowAddModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50/50 p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products Catalog</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your travel products — packages, flights, hotels, tours &amp; more</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
            <i className="ri-download-2-line" />
            Export
          </button>
          <button
            onClick={() => { setEditProduct(null); setShowAddModal(true); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: brand }}
          >
            <i className="ri-add-line" />
            Add Product
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-5 mb-8">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl mb-4" style={{ backgroundColor: `${kpi.color}15` }}>
              <i className={`${kpi.icon} text-lg`} style={{ color: kpi.color }} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
            <p className="text-sm font-medium text-gray-700 mt-1">{kpi.label}</p>
            <p className="text-xs text-gray-400 mt-0.5">{kpi.sub}</p>
          </div>
        ))}
      </div>

      {/* Filters Bar */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-xs">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Search products, destinations, tags..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'All' | 'Active' | 'Inactive' | 'Sold Out')}
              className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none cursor-pointer"
            >
              <option value="All">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Sold Out">Sold Out</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'sellPrice' | 'margin' | 'booked')}
              className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none cursor-pointer"
            >
              <option value="booked">Sort: Most Booked</option>
              <option value="sellPrice">Sort: Highest Price</option>
              <option value="margin">Sort: Best Margin</option>
              <option value="name">Sort: Name A–Z</option>
            </select>
            <div className="flex items-center bg-gray-100 rounded-xl p-1">
              <button
                onClick={() => setView('grid')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium cursor-pointer transition-all ${view === 'grid' ? 'bg-white text-gray-800' : 'text-gray-500'}`}
              >
                <i className="ri-grid-line" />
              </button>
              <button
                onClick={() => setView('list')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium cursor-pointer transition-all ${view === 'list' ? 'bg-white text-gray-800' : 'text-gray-500'}`}
              >
                <i className="ri-list-check-2" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2 flex-wrap mb-6">
        {categories.map((cat) => {
          const active = categoryTab === cat;
          const cfg = cat !== 'All' ? categoryConfig[cat as ProductCategory] : null;
          const count = cat === 'All' ? products.length : products.filter((p) => p.category === cat).length;
          return (
            <button
              key={cat}
              onClick={() => setCategoryTab(cat)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium cursor-pointer transition-all whitespace-nowrap ${
                active ? 'text-white' : 'bg-white border border-gray-100 text-gray-600 hover:border-gray-200'
              }`}
              style={active ? { backgroundColor: cfg ? cfg.color : brand } : {}}
            >
              {cfg && <i className={`${cfg.icon} text-xs`} />}
              {cat}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          Showing <span className="font-semibold text-gray-700">{filtered.length}</span> of {products.length} products
        </p>
        {search && (
          <button onClick={() => setSearch('')} className="text-sm text-gray-400 hover:text-gray-600 cursor-pointer flex items-center gap-1">
            <i className="ri-close-line" />
            Clear search
          </button>
        )}
      </div>

      {/* Product Grid / List */}
      {filtered.length > 0 ? (
        view === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((p) => (
              <ProductCard key={p.id} product={p} view="grid" onView={setSelectedProduct} />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((p) => (
              <ProductCard key={p.id} product={p} view="list" onView={setSelectedProduct} />
            ))}
          </div>
        )
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 py-24 text-center">
          <div className="w-14 h-14 flex items-center justify-center rounded-2xl mx-auto mb-4" style={{ backgroundColor: `${brand}10` }}>
            <i className="ri-price-tag-3-line text-2xl" style={{ color: brand }} />
          </div>
          <p className="font-semibold text-gray-700 mb-1">No products found</p>
          <p className="text-sm text-gray-400 mb-6">Try a different search or category filter</p>
          <button
            onClick={() => { setSearch(''); setCategoryTab('All'); }}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Product Detail Drawer */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onEdit={handleEdit}
        />
      )}

      {/* Add / Edit Modal */}
      {showAddModal && (
        <AddProductModal
          onClose={() => { setShowAddModal(false); setEditProduct(null); }}
          editProduct={editProduct}
        />
      )}
    </div>
  );
}
